import React, { useState, useEffect, useCallback } from 'react';
import { FaCode, FaFileAlt, FaTasks, FaPlus, FaTimes } from 'react-icons/fa';
import { apiFetch } from '../../../utils/apiAdmin';
import { lookupScript } from '../../../components/tmeWiki/tmeWikiNodeSupport';

/**
 * A page's Links panel, and the three pickers that add to it.
 *
 * Planning/TME-WIKI-2026-09-02.md §9.1, §9.2, §9.3, §9.5, §9.5.1, D5, D23.
 *
 * ⚠️ THE PANEL IS THE RECORD; THE INLINE NODE IS DISPLAY (D5). Removing an
 * inline reference from the body does NOT unlink anything - unlinking happens
 * here - so a stray keystroke cannot silently break the Script Repository's
 * "used by" list.
 *
 * ⚠️ A SCRIPT'S NAME IS RESOLVED LIVE (D23). The stored `targetLabel` is the
 * fallback for a script that no longer exists, shown greyed and marked
 * "(deleted)". Revision 2 of the plan had AdminScriptsFunction refresh the
 * stored label on rename - a grant §13.2 refuses, and the exact shape Bill
 * rejected for task renames.
 */

const API = `${process.env.REACT_APP_API_URL}/admin/tme-wiki`;

const KINDS = [
    { kind: 'script', label: 'Scripts', icon: FaCode },
    { kind: 'page', label: 'Pages', icon: FaFileAlt },
    { kind: 'task', label: 'Tasks', icon: FaTasks }
];

/**
 * How a row is labelled, and whether it is greyed (§9.5, §9.5.1).
 *
 * ⚠️ A CROSS-WIKI TARGET IS NOT GREYED, DELIBERATELY (I3). D19 made a link into
 * another wiki legitimate, and that wiki's page list is not loaded - so greying
 * it would mean either one GET per cross-wiki row on every panel open, or
 * mirroring the target's `archivedAt` onto the link row, a snapshot that goes
 * stale the moment the target is archived. The banner on open is the honest
 * answer: the panel does not claim to know, and the page itself says so.
 */
export function describeLink(link, { wikiId, pages }) {
    if (link.kind === 'script') {
        const live = lookupScript(link.targetId);
        return live
            ? { text: live.name, muted: false }
            : { text: `${link.targetLabel} (deleted)`, muted: true };
    }

    if (link.kind === 'page') {
        const [targetWikiId, targetPageId] = String(link.targetId).split('#');
        if (targetWikiId !== wikiId) {
            // Another wiki: legitimate, and not something this list knows about.
            return { text: `${link.targetLabel} (another wiki)`, muted: false };
        }
        const target = (pages || []).find(p => p.pageId === targetPageId);
        if (target?.archivedAt) return { text: `${link.targetLabel} (archived)`, muted: true };
        return { text: link.targetLabel, muted: false };
    }

    return { text: link.targetLabel, muted: false };
}

const TmeWikiLinksPanel = ({
    wikiId, page, pages, scripts, onRefreshScripts, expanded, onToggle, refreshKey = 0,
    // The picker is CONTROLLED, because the editor's inline-insert buttons
    // open the same one (§9.3) - and the row must be written before the node
    // is inserted, which only the pane can sequence.
    picker = null, onPickerChange, onLinked
}) => {
    const [links, setLinks] = useState([]);
    const [availableTasks, setAvailableTasks] = useState([]);
    // Pages in OTHER wikis, loaded when the page picker opens (review finding
    // 6). D19 made a cross-wiki link legitimate, and until this the picker
    // offered the current wiki alone, so none could be made from the UI.
    const [otherWikiPages, setOtherWikiPages] = useState([]);
    const [loadingOtherWikis, setLoadingOtherWikis] = useState(false);
    const [filter, setFilter] = useState('');
    const [taskText, setTaskText] = useState('');
    const [busy, setBusy] = useState(false);

    const pageId = page?.pageId;

    const fetchLinks = useCallback(async () => {
        if (!pageId) return;
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${pageId}/links`);
            if (!response.ok) return;
            const data = await response.json();
            setLinks(data.links || []);
        } catch (err) {
            console.error('Failed to load TME wiki links:', err);
        }
    }, [wikiId, pageId]);

    useEffect(() => {
        fetchLinks();
    }, [fetchLinks]);

    // LIV-14: the pane bumps this on every tme_wiki_updated. This panel is
    // mounted INSIDE the pane and fetches its own contents, so the refresh
    // reaches it as a key rather than as a call - the D7 shape, one level
    // lower. Keyed on the KEY alone, deliberately: adding fetchLinks to the
    // deps would re-run it on every page change as well, which the effect
    // above already does.
    useEffect(() => {
        if (!refreshKey) return;
        fetchLinks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const openPicker = async (kind) => {
        setFilter('');
        setTaskText('');
        onPickerChange?.(kind);
        // The script list is refreshed HERE rather than per page (H3): a script
        // created since this shell mounted should still be offered.
        if (kind === 'script') onRefreshScripts?.();
        if (kind === 'page') {
            // The other wikis' page lists, on demand and only while the picker
            // is open - the pattern the task modal's picker already uses. The
            // listings are PROJECTED (§6.7): titles and ids, no bodies. The
            // current wiki's list is already loaded and is not re-read.
            setLoadingOtherWikis(true);
            try {
                const wikisResponse = await apiFetch(`${API}/wikis`);
                const { wikis = [] } = wikisResponse.ok ? await wikisResponse.json() : {};
                const others = (wikis || []).filter(w => w.wikiId && w.wikiId !== wikiId && !w.archivedAt);
                const perWiki = await Promise.all(others.map(async (wiki) => {
                    const response = await apiFetch(`${API}/wikis/${wiki.wikiId}/pages`);
                    if (!response.ok) return [];
                    const { pages: theirs = [] } = await response.json();
                    return (theirs || [])
                        .filter(p => !p.archivedAt)
                        .map(p => ({
                            targetId: `${wiki.wikiId}#${p.pageId}`,
                            // The label STORED on the row is the page title alone,
                            // because the rename refresh (§9.5) writes the title
                            // there; the wiki's name is shown beside it, not in it.
                            targetLabel: p.title,
                            hint: wiki.name
                        }));
                }));
                setOtherWikiPages(perWiki.flat());
            } catch (err) {
                console.error('Failed to load other wikis for the page picker:', err);
                setOtherWikiPages([]);
            } finally {
                setLoadingOtherWikis(false);
            }
        }
        if (kind === 'task' && availableTasks.length === 0) {
            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/scripts/available-tasks`
                );
                if (response.ok) {
                    const data = await response.json();
                    // ⚠️ THE ROUTE RETURNS OBJECTS, NOT NAMES: `{ taskName, taskType,
                    // … }`, the shape the Script Repository reads as `t.taskName`.
                    // This panel treated each as a string, so the candidate's label
                    // was an object, React refused to render it, and the whole page
                    // went white on the first search (2026-09-02, first preview
                    // use). Only the name and the type are kept.
                    setAvailableTasks((data.tasks || [])
                        .filter(t => t && typeof t.taskName === 'string' && t.taskName.trim())
                        .map(t => ({ name: t.taskName.trim(), type: t.taskType })));
                }
            } catch (err) {
                console.error('Failed to load available tasks:', err);
            }
        }
    };

    const addLink = async ({ kind, targetId, targetLabel }) => {
        setBusy(true);
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${pageId}/links`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kind, targetId, targetLabel })
            });
            const data = await response.json();
            if (!response.ok) {
                // The 51st link, a duplicate, a validation failure - all named.
                // ⚠️ AND NOTHING IS INSERTED (§9.3, H4): the row is the record,
                // so a node in the body that no row knows about is exactly the
                // drift D5 exists to prevent.
                window.alert(data.message || 'Could not add the link');
                return false;
            }
            onPickerChange?.(null);
            await fetchLinks();
            onLinked?.(data);
            return true;
        } finally {
            setBusy(false);
        }
    };

    const removeLink = async (link) => {
        // ⚠️ THE TARGET IS ENCODED. A page target is `<wikiId>#<pageId>`, and a
        // raw `#` is read by the browser as a fragment: the request would arrive
        // truncated, with nothing to say so.
        const response = await apiFetch(
            `${API}/wikis/${wikiId}/pages/${pageId}/links/${link.kind}/${encodeURIComponent(link.targetId)}`,
            { method: 'DELETE' }
        );
        if (!response.ok) {
            const data = await response.json();
            window.alert(data.message || 'Could not remove the link');
        }
        await fetchLinks();
    };

    if (!page) return null;

    const grouped = kind => links.filter(l => l.kind === kind);

    const candidates = () => {
        const needle = filter.trim().toLowerCase();
        if (picker === 'script') {
            return (scripts || [])
                .filter(s => !needle || (s.name || '').toLowerCase().includes(needle))
                .map(s => ({ targetId: s.scriptId, targetLabel: s.name }));
        }
        if (picker === 'page') {
            const here = (pages || [])
                .filter(p => p.pageId !== pageId)
                .map(p => ({ targetId: `${wikiId}#${p.pageId}`, targetLabel: p.title }));
            // This wiki first, then the others, each row saying which wiki.
            return [...here, ...otherWikiPages]
                .filter(c => !needle || (c.targetLabel || '').toLowerCase().includes(needle));
        }
        return availableTasks
            .filter(t => !needle || t.name.toLowerCase().includes(needle))
            .map(t => ({ targetId: t.name, targetLabel: t.name, hint: t.type }));
    };

    return (
        <div className="mt-4 border-t border-gray-200 pt-3">
            <button
                type="button"
                onClick={onToggle}
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
            >
                {expanded ? '▼' : '►'} Links ({links.length})
            </button>

            {expanded && (
                <div className="mt-2 space-y-3">
                    {KINDS.map(({ kind, label, icon: Icon }) => (
                        <div key={kind}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                    <Icon className="w-3 h-3" />
                                    {label} ({grouped(kind).length})
                                </span>
                                <button
                                    type="button"
                                    onClick={() => openPicker(kind)}
                                    className="text-primary-600 hover:text-primary-800"
                                    title={`Link a ${kind}`}
                                >
                                    <FaPlus className="w-3 h-3" />
                                </button>
                            </div>

                            {grouped(kind).length === 0 && (
                                <p className="text-xs text-gray-400 italic pl-4">None</p>
                            )}

                            {grouped(kind).map(link => {
                                const described = describeLink(link, { wikiId, pages });
                                return (
                                    <div
                                        key={`${link.kind}#${link.targetId}`}
                                        className="flex items-center justify-between pl-4 py-1"
                                    >
                                        <span className={`text-sm truncate ${described.muted ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                                            {described.text}
                                        </span>
                                        <button
                                            type="button"
                                            aria-label={`Unlink ${link.targetLabel}`}
                                            onClick={() => removeLink(link)}
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <FaTimes className="w-3 h-3" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {picker && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[70]">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-5 max-h-[80vh] flex flex-col">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Link a {picker}
                        </h3>
                        {picker === 'task' && (
                            // ⚠️ VERIFIED, not assumed: /admin/scripts/available-tasks
                            // reads TEMPLATE tasks - one Query per template
                            // partition, no scan - and drops any name containing
                            // "meeting". So the list is template task names only,
                            // and the label says so rather than implying it covers
                            // live projects.
                            <p className="text-xs text-gray-500 mb-2">
                                Suggestions are TEMPLATE task names. Any other name can be typed -
                                links are keyed by name (D4), so it will match wherever that task appears.
                            </p>
                        )}

                        <input
                            type="text"
                            aria-label={`Filter ${picker}s`}
                            placeholder="Filter…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-2"
                        />

                        {picker === 'task' && (
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    aria-label="Task name"
                                    placeholder="Or type a task name"
                                    value={taskText}
                                    onChange={(e) => setTaskText(e.target.value)}
                                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                                />
                                <button
                                    type="button"
                                    disabled={busy || !taskText.trim()}
                                    onClick={() => addLink({
                                        kind: 'task', targetId: taskText.trim(), targetLabel: taskText.trim()
                                    })}
                                    className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded disabled:opacity-50"
                                >
                                    Link
                                </button>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto">
                            {picker === 'page' && loadingOtherWikis && (
                                <p className="text-xs text-gray-400 italic py-1">Loading other wikis…</p>
                            )}
                            {candidates().length === 0 && !loadingOtherWikis && (
                                <p className="text-sm text-gray-500 italic py-4">Nothing to choose from.</p>
                            )}
                            {candidates().map(candidate => (
                                <button
                                    key={candidate.targetId}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => addLink({
                                        kind: picker, targetId: candidate.targetId, targetLabel: candidate.targetLabel
                                    })}
                                    className="block w-full text-left px-3 py-2 rounded hover:bg-gray-100 text-sm"
                                >
                                    {candidate.targetLabel}
                                    {candidate.hint && (
                                        <span className="ml-2 text-xs text-gray-400">{candidate.hint}</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-end mt-3 pt-3 border-t">
                            <button
                                type="button"
                                onClick={() => onPickerChange?.(null)}
                                className="px-4 py-2 text-gray-700 hover:text-gray-900"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TmeWikiLinksPanel;
